import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ParcelOrderService from "../../services/parcel-order.service";
import OrderCard from "../../components/OrderCard";
import { HiMagnifyingGlass, HiArrowPath, HiMapPin } from "react-icons/hi2";

export default function TrackPage() {
  const location = useLocation();
  const initialRef = new URLSearchParams(location.search).get("ref") ?? "";

  const [ref, setRef] = useState(initialRef);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (initialRef) search(initialRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function search(refOverride) {
    const query = (refOverride ?? ref).trim();
    if (!query) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const data = await ParcelOrderService.findOrder(query);
      if (!data || (Array.isArray(data) && data.length === 0)) {
        setError("No order found with that reference number.");
      } else {
        setOrder(Array.isArray(data) ? data[0] : data);
      }
    } catch {
      setError("Order not found. Please check the reference number.");
    } finally {
      setLoading(false);
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    search();
  }

  return (
    <div className="px-4 py-8 lg:px-8">
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900">Track Parcel</h1>
        <p className="text-sm text-gray-400 mt-0.5">
          Enter a reference number to see live shipment status
        </p>
      </div>

      {/* Search form */}
      <form onSubmit={handleSubmit} className="flex gap-2 mb-6">
        <div className="relative flex-1">
          <HiMagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={ref}
            onChange={(e) => setRef(e.target.value)}
            placeholder="e.g. PCL-2024-00123"
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-400 shadow-sm"
          />
        </div>
        <button
          type="submit"
          disabled={!ref.trim() || loading}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors shadow-sm"
        >
          {loading ? (
            <HiArrowPath className="h-4 w-4 animate-spin" />
          ) : (
            "Track"
          )}
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 text-sm mb-5">
          {error}
        </div>
      )}

      {/* Result */}
      {order && (
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
            Result
          </p>
          <OrderCard order={order} />
        </div>
      )}

      {/* Empty / hint */}
      {!order && !error && !loading && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm flex flex-col items-center justify-center py-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-violet-50 flex items-center justify-center mb-4">
            <HiMapPin className="h-7 w-7 text-violet-400" />
          </div>
          <p className="text-gray-600 font-semibold text-sm mb-1">
            Enter a reference to track
          </p>
          <p className="text-gray-400 text-xs max-w-xs">
            You can find the reference number in your order list or on your
            confirmation.
          </p>
        </div>
      )}
    </div>
  );
}
